import React, { useState, useEffect, useCallback } from "react";
import { API_ENDPOINTS } from "../../config/api";
import { formatDate } from "../../utils/dateFormatting";
import { useNavigateWithCommandClick } from "../../hooks/useNavigateWithCommandClick";

import Button from "../../components/Button";
import { Table, Row, Column, ActionsColumn } from "../../components/Table";
import { LoadingState, EmptyState } from "../../components/CommonStates";
import Modal, {
  ModalTitle,
  ModalDescription,
  ModalActions
} from "../../components/Modal";
import { Form, FormGroup, Label } from "../../components/Form";
import { Input } from "../../components/Input";
import { Dropdown } from "../../components/Dropdown";

function UsersTab({ user, onError, onSuccess }) {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [updatingUsers, setUpdatingUsers] = useState(new Set());
  const [deletingUsers, setDeletingUsers] = useState(new Set());
  const [deleteConfirmation, setDeleteConfirmation] = useState(null);
  const [editModalUser, setEditModalUser] = useState(null);
  const [editFormData, setEditFormData] = useState({
    username: "",
    isAdmin: false
  });
  const [resetPasswordModal, setResetPasswordModal] = useState(null);
  const [resettingPassword, setResettingPassword] = useState(new Set());
  const [copiedToClipboard, setCopiedToClipboard] = useState(false);
  const navigate = useNavigateWithCommandClick();

  const fetchUsers = useCallback(async () => {
    setLoading(true);
    onError("");

    try {
      const token = localStorage.getItem("token");
      const response = await fetch(API_ENDPOINTS.adminUsers, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json"
        }
      });

      const data = await response.json();

      if (response.ok) {
        setUsers(data.users || []);
      } else {
        onError(data.error || "Failed to fetch users");
      }
    } catch (error) {
      onError("Network error. Please try again.");
    } finally {
      setLoading(false);
    }
  }, [onError]);

  const updateUserAdminStatus = async (userId, isAdmin) => {
    onError("");
    setUpdatingUsers((prev) => new Set(prev).add(userId));

    try {
      const token = localStorage.getItem("token");
      const response = await fetch(API_ENDPOINTS.updateAdminStatus(userId), {
        method: "PUT",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json"
        },
        body: JSON.stringify({ isAdmin })
      });

      const data = await response.json();

      if (response.ok) {
        setUsers((prevUsers) =>
          prevUsers.map((user) =>
            user.id === userId
              ? {
                  ...user,
                  isAdmin: data.user.isAdmin,
                  updatedAt: data.user.updatedAt
                }
              : user
          )
        );
        onSuccess(
          data.message ||
            `User ${
              isAdmin ? "promoted to admin" : "admin status removed"
            } successfully`
        );
      } else {
        onError(data.error || "Failed to update user admin status");
      }
    } catch (error) {
      onError("Network error. Please try again.");
    } finally {
      setUpdatingUsers((prev) => {
        const newSet = new Set(prev);
        newSet.delete(userId);
        return newSet;
      });
    }
  };

  const updateUserUsername = async (userId, newUsername) => {
    onError("");
    setUpdatingUsers((prev) => new Set(prev).add(userId));

    try {
      const token = localStorage.getItem("token");
      const response = await fetch(API_ENDPOINTS.updateUserUsername(userId), {
        method: "PUT",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json"
        },
        body: JSON.stringify({ username: newUsername })
      });

      const data = await response.json();

      if (response.ok) {
        setUsers((prevUsers) =>
          prevUsers.map((user) =>
            user.id === userId
              ? {
                  ...user,
                  username: data.user.username,
                  updatedAt: data.user.updatedAt
                }
              : user
          )
        );

        onSuccess(data.message || "Username updated successfully");
      } else {
        onError(data.error || "Failed to update username");
      }
    } catch (error) {
      onError("Network error. Please try again.");
    } finally {
      setUpdatingUsers((prev) => {
        const newSet = new Set(prev);
        newSet.delete(userId);
        return newSet;
      });
    }
  };

  const resetUserPassword = async (userId) => {
    onError("");
    setResettingPassword((prev) => new Set(prev).add(userId));

    try {
      const token = localStorage.getItem("token");
      const response = await fetch(API_ENDPOINTS.resetUserPassword(userId), {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json"
        }
      });

      const data = await response.json();

      if (response.ok) {
        // Show the temporary password to the admin
        setResetPasswordModal({
          user: users.find((u) => u.id === userId),
          temporaryPassword: data.temporaryPassword
        });
        onSuccess(
          "Password reset successfully. Please copy the temporary password and send it to the user."
        );
      } else {
        onError(data.error || "Failed to reset password");
      }
    } catch (error) {
      onError("Network error. Please try again.");
    } finally {
      setResettingPassword((prev) => {
        const newSet = new Set(prev);
        newSet.delete(userId);
        return newSet;
      });
    }
  };

  const deleteUser = async (userId) => {
    onError("");
    setDeletingUsers((prev) => new Set(prev).add(userId));

    try {
      const token = localStorage.getItem("token");
      const response = await fetch(API_ENDPOINTS.deleteUser(userId), {
        method: "DELETE",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json"
        }
      });

      const data = await response.json();

      if (response.ok) {
        setUsers((prevUsers) => prevUsers.filter((user) => user.id !== userId));
        onSuccess(data.message || "User deleted successfully");
      } else {
        onError(data.error || "Failed to delete user");
      }
    } catch (error) {
      onError("Network error. Please try again.");
    } finally {
      setDeletingUsers((prev) => {
        const newSet = new Set(prev);
        newSet.delete(userId);
        return newSet;
      });
      setDeleteConfirmation(null);
    }
  };

  const handleDeleteClick = (userToDelete) => {
    setDeleteConfirmation(userToDelete);
  };

  const handleDeleteConfirm = () => {
    if (deleteConfirmation) {
      deleteUser(deleteConfirmation.id);
    }
  };

  const handleDeleteCancel = () => {
    setDeleteConfirmation(null);
  };

  const handleEditClick = (userToEdit) => {
    setEditModalUser(userToEdit);
    setEditFormData({
      username: userToEdit.username,
      isAdmin: userToEdit.isAdmin
    });
    onError("");
  };

  const handleEditCancel = () => {
    setEditModalUser(null);
    setEditFormData({
      username: "",
      isAdmin: false
    });
    onError("");
  };

  const handleResetPasswordClick = (userToReset) => {
    setResetPasswordModal({
      user: userToReset,
      temporaryPassword: null
    });
    onError("");
  };

  const handleResetPasswordConfirm = () => {
    if (resetPasswordModal?.user) {
      resetUserPassword(resetPasswordModal.user.id);
    }
  };

  const handleResetPasswordCancel = () => {
    setResetPasswordModal(null);
    onError("");
  };

  const handleResetPasswordClose = () => {
    setResetPasswordModal(null);
  };

  const handleEditFormChange = (field, value) => {
    setEditFormData((prev) => ({
      ...prev,
      [field]: value
    }));
  };

  const handleEditSave = async () => {
    if (!editModalUser) return;

    const { username, isAdmin } = editFormData;

    if (!username || !username.trim()) {
      onError("Username cannot be empty");
      return;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(username.trim())) {
      onError("Please enter a valid email address");
      return;
    }

    const usernameChanged = username.trim() !== editModalUser.username;
    const adminStatusChanged = isAdmin !== editModalUser.isAdmin;

    if (!usernameChanged && !adminStatusChanged) {
      handleEditCancel();
      return;
    }

    try {
      // Update username if changed
      if (usernameChanged) {
        await updateUserUsername(editModalUser.id, username.trim());
      }

      // Update admin status if changed
      if (adminStatusChanged) {
        await updateUserAdminStatus(editModalUser.id, isAdmin);
      }

      handleEditCancel();
    } catch (error) {
      // Error handling is already done in the individual update functions
    }
  };

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  if (loading && !users.length) {
    return <LoadingState>Loading users...</LoadingState>;
  }

  if (users.length === 0) {
    return (
      <EmptyState>
        <h4>No users found</h4>
        <p>There are no users in the system.</p>
      </EmptyState>
    );
  }

  return (
    <>
      <Table columns="1fr 0.5fr 1fr 400px">
        <Row>
          <Column>Username</Column>
          <Column>Type</Column>
          <Column>Last login</Column>
          <ActionsColumn>Actions</ActionsColumn>
        </Row>

        {users.map((userItem) => (
          <Row key={userItem.id}>
            <Column data-label="Username">{userItem.username}</Column>
            <Column data-label="Admin">
              {userItem.isAdmin ? "Admin" : "User"}
            </Column>
            <Column data-label="Last Login">
              {formatDate(userItem.lastLogin)}
            </Column>
            <ActionsColumn data-label="Actions">
              <Button
                onClick={() => handleEditClick(userItem)}
                disabled={
                  updatingUsers.has(userItem.id) ||
                  deletingUsers.has(userItem.id)
                }
              >
                Edit info
              </Button>
              <Button
                onClick={() => {
                  // We'll need to pass this function from parent
                  // For now, let's emit an event or call a callback
                  if (window.handleBotSelectionClick) {
                    window.handleBotSelectionClick(userItem);
                  }
                }}
                disabled={
                  deletingUsers.has(userItem.id) ||
                  updatingUsers.has(userItem.id)
                }
              >
                Edit bots
              </Button>
              <Button
                onClick={(event) =>
                  navigate(
                    `/admin/chat-logs/specific-user/${encodeURIComponent(
                      userItem.username
                    )}`,
                    {},
                    event
                  )
                }
                disabled={
                  deletingUsers.has(userItem.id) ||
                  updatingUsers.has(userItem.id)
                }
                style={{ whiteSpace: "nowrap" }}
              >
                View logs
              </Button>
            </ActionsColumn>
          </Row>
        ))}
      </Table>

      {/* Edit User Modal */}
      <Modal isOpen={!!editModalUser} onClose={handleEditCancel}>
        <ModalTitle>Edit user info</ModalTitle>
        <Form>
          {editModalUser && (
            <div
              style={{
                marginBottom: "1.5rem",
                padding: "0.75rem",
                backgroundColor: "#f8f9fa",
                borderRadius: "8px"
              }}
            >
              <div style={{ fontSize: "0.875rem", color: "#666" }}>
                Created: {formatDate(editModalUser.createdAt)}
              </div>
            </div>
          )}

          <FormGroup>
            <Label>Username</Label>
            <Input
              type="email"
              value={editFormData.username}
              onChange={(e) => handleEditFormChange("username", e.target.value)}
              disabled={updatingUsers.has(editModalUser?.id)}
              placeholder="Enter email address"
              style={{ width: "100%", marginBottom: "16px" }}
            />
          </FormGroup>

          <FormGroup>
            <Label>Type</Label>
            <Dropdown
              value={editFormData.isAdmin ? "admin" : "user"}
              onChange={(e) =>
                handleEditFormChange("isAdmin", e.target.value === "admin")
              }
              disabled={updatingUsers.has(editModalUser?.id)}
              style={{ width: "100%" }}
            >
              <option value="user">User</option>
              <option value="admin">Admin</option>
            </Dropdown>
          </FormGroup>

          <FormGroup>
            <Label>Actions</Label>
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                gap: "0.5rem"
              }}
            >
              <Button
                type="button"
                variant="secondary"
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  handleResetPasswordClick(editModalUser);
                }}
                disabled={
                  updatingUsers.has(editModalUser?.id) ||
                  resettingPassword.has(editModalUser?.id)
                }
                style={{ width: "100%" }}
              >
                {resettingPassword.has(editModalUser?.id)
                  ? "Resetting..."
                  : "Reset password"}
              </Button>
              {editModalUser?.id !== user.id && (
                <Button
                  type="button"
                  variant="danger"
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    handleDeleteClick(editModalUser);
                    handleEditCancel();
                  }}
                  disabled={
                    updatingUsers.has(editModalUser?.id) ||
                    deletingUsers.has(editModalUser?.id)
                  }
                  style={{ width: "100%" }}
                >
                  Delete user
                </Button>
              )}
            </div>
          </FormGroup>

          <ModalActions>
            <Button
              variant="secondary"
              onClick={handleEditCancel}
              disabled={updatingUsers.has(editModalUser?.id)}
            >
              Cancel
            </Button>
            <Button
              onClick={handleEditSave}
              disabled={updatingUsers.has(editModalUser?.id)}
            >
              {updatingUsers.has(editModalUser?.id) ? "Saving..." : "Save"}
            </Button>
          </ModalActions>
        </Form>
      </Modal>

      {/* Delete Confirmation Modal */}
      <Modal isOpen={!!deleteConfirmation} onClose={handleDeleteCancel}>
        <ModalTitle>
          Delete account for {deleteConfirmation?.username}?
        </ModalTitle>
        <ModalDescription>
          Are you sure you want to permanently delete this user?
        </ModalDescription>
        <ModalActions>
          <Button variant="secondary" onClick={handleDeleteCancel}>
            Cancel
          </Button>
          <Button
            variant="danger"
            onClick={handleDeleteConfirm}
            disabled={deletingUsers.has(deleteConfirmation?.id)}
          >
            {deletingUsers.has(deleteConfirmation?.id)
              ? "Deleting..."
              : "Delete"}
          </Button>
        </ModalActions>
      </Modal>

      {/* Password Reset Confirmation Modal */}
      <Modal
        isOpen={!!resetPasswordModal && !resetPasswordModal.temporaryPassword}
        onClose={handleResetPasswordCancel}
      >
        <ModalTitle>
          Reset password for {resetPasswordModal?.user?.username}?
        </ModalTitle>
        <ModalDescription>
          This will generate a new temporary password for the user. They will be
          required to change it on their next login.
        </ModalDescription>
        <ModalActions>
          <Button variant="secondary" onClick={handleResetPasswordCancel}>
            Cancel
          </Button>
          <Button
            variant="danger"
            onClick={handleResetPasswordConfirm}
            disabled={resettingPassword.has(resetPasswordModal?.user?.id)}
          >
            {resettingPassword.has(resetPasswordModal?.user?.id)
              ? "Resetting..."
              : "Reset password"}
          </Button>
        </ModalActions>
      </Modal>

      {/* Temporary Password Display Modal */}
      <Modal
        isOpen={!!resetPasswordModal && !!resetPasswordModal.temporaryPassword}
        onClose={handleResetPasswordClose}
      >
        <ModalTitle>Temporary Password Generated</ModalTitle>
        <ModalDescription>
          A new temporary password has been generated for{" "}
          <strong>{resetPasswordModal?.user?.username}</strong>. Please copy
          this password and send it to the user via your manual communication
          channel.
        </ModalDescription>

        <FormGroup>
          <Label>Temporary Password</Label>
          <Input
            type="text"
            value={resetPasswordModal?.temporaryPassword || ""}
            readOnly
            style={{
              width: "100%",
              marginBottom: "16px",
              fontFamily: "monospace",
              fontSize: "14px",
              backgroundColor: "#f5f5f5"
            }}
          />
        </FormGroup>

        <ModalActions>
          <Button
            onClick={() => {
              navigator.clipboard.writeText(
                resetPasswordModal?.temporaryPassword || ""
              );
              setCopiedToClipboard(true);
              setTimeout(() => {
                setCopiedToClipboard(false);
              }, 2000);
            }}
          >
            {copiedToClipboard ? "Copied!" : "Copy to clipboard"}
          </Button>
          <Button variant="secondary" onClick={handleResetPasswordClose}>
            Close
          </Button>
        </ModalActions>
      </Modal>
    </>
  );
}

export default UsersTab;
